import dayjs from 'dayjs';
import React from 'react';
import styled from 'styled-components';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { useLocation, useParams } from 'react-router-dom';
import { Refresh as RefreshIcon } from '@material-ui/icons';
import { replace as Replace } from 'redux-first-history';
import { parse } from 'query-string';
import { palette, SearchInput } from '@dev/ee-components';
import { DeliveryLogType } from '@dev/ee-api/ee-api-models';
import { safelyDecodeBase64Url } from '@dev/ee-tools';

import useMobile from '@/services/hooks/useMobile';
import { getReportsState } from '@/store/reports/actions';
import { debounce } from '@/services/tools/debounce';
import { EventLogByChannelNameStart } from '@/store/reports/actions';
import { EventLogParamsByChannelName } from '@/store/reports/types';
import { LONG_DATE_FORMAT } from '@/services/constants';
import { addParamToUrl } from '@/services/tools/handleUrlParams';

const TopContainer = styled.div<{ isMobile: boolean }>`
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: ${(props) => (props.isMobile ? '0 20px 32px' : '0 48px 32px')};
`;

const StyledRefreshIcon = styled.div`
    margin-right: 18px;
    font-size: 28px;
    color: ${palette.gray[6]};
    &:hover {
        cursor: pointer;
    }
`;

const SearchInputComponent = (): JSX.Element => {
    const { t } = useTranslation();
    const query = parse(location.search);
    const dispatch = useDispatch();
    const { name } = useParams<{ name: string }>();
    const { eventLogParamsByChannelName } = useSelector(getReportsState);

    const searchLogsWhereEmailStartWith = (event?): void => {
        const searchString = event?.target?.value ?? '';
        const params: EventLogParamsByChannelName = {
            statuses: eventLogParamsByChannelName?.statuses,
            offset: 0,
            limit: eventLogParamsByChannelName?.limit,
            channelName: safelyDecodeBase64Url(name),
            messageCategory: eventLogParamsByChannelName?.messageCategory,
            email: searchString,
            lastEvent: true,
            from: eventLogParamsByChannelName.from,
            to: eventLogParamsByChannelName.to,
        };
        const newSearch = addParamToUrl({ search: searchString, page: 1 });
        dispatch(Replace({ search: newSearch }));
        debounce((): void => {
            dispatch(EventLogByChannelNameStart(params));
        }, 500);
    };

    return (
        <SearchInput
            id="inProgressSearchInput"
            name="searchInput"
            defaultValue={query?.search}
            onChange={(event) => searchLogsWhereEmailStartWith(event)}
            onDelete={searchLogsWhereEmailStartWith}
            placeholder={t('searchforcontacts')}
            type="text"
        />
    );
};

export const SearchAndRefresh = (): JSX.Element => {
    const { eventLogParamsByChannelName } = useSelector(getReportsState);
    const { name } = useParams<{ name: string }>();
    const dispatch = useDispatch();
    const location = useLocation();
    const isMobile = useMobile();

    const query = parse(location.search);
    const params: EventLogParamsByChannelName = {
        statuses: query?.statuses
            ? ([...query.statuses] as DeliveryLogType[])
            : [DeliveryLogType.ReadyToSend, DeliveryLogType.WaitingToRetry, DeliveryLogType.Sending],
        channelName: safelyDecodeBase64Url(name),
        email: query?.search as string,
        messageCategory: eventLogParamsByChannelName?.messageCategory,
        offset:
            (parseInt(query?.page as string, 10) - 1) * parseInt(query?.perPage as string, 10) ||
            eventLogParamsByChannelName?.offset,
        limit:
            (parseInt(query?.page as string, 10) - 1) * parseInt(query?.perPage as string, 10) ||
            eventLogParamsByChannelName?.limit,
        lastEvent: true,
        from: dayjs(dayjs().format(LONG_DATE_FORMAT)).subtract(2, 'day').format(LONG_DATE_FORMAT),
        to: dayjs().format(LONG_DATE_FORMAT),
    };

    return (
        <TopContainer isMobile={isMobile}>
            {!isMobile && (
                <StyledRefreshIcon>
                    <RefreshIcon
                        onClick={() => {
                            dispatch(EventLogByChannelNameStart(params));
                        }}
                    />
                </StyledRefreshIcon>
            )}
            <SearchInputComponent />
        </TopContainer>
    );
};

export default SearchAndRefresh;
